import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { SubscriptionDialog } from '@/components/ui/subscription-dialog';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import {
  LayoutGrid, Plus, CheckSquare, Square, Star, StarOff,
  Trash2, Loader2, ArrowUpDown, Filter,
} from 'lucide-react';
import { fsopApi } from '@/services/api';

interface FieldOption { key: string; label: string; }

interface ViewConfig {
  id: number; name: string; selectedFields: string[];
  sortBy: string; sortOrder: string; isDefault: boolean;
  pageSize: number; filters: Record<string, string>;
  createdAt: string; updatedAt: string;
}

const BASE_FIELDS: FieldOption[] = [
  {key:'airDate',label:'飞行日期'},{key:'airNum',label:'航班号'},{key:'airModel',label:'机型'},
  {key:'airline',label:'航线'},{key:'departure',label:'出发地'},{key:'arrival',label:'到达地'},
  {key:'flightTime',label:'飞行时间(分钟)'},{key:'nightTime',label:'夜间时间(分钟)'},
  {key:'dayTime',label:'昼间时间(分钟)'},{key:'recordType',label:'记录类型'},{key:'state',label:'状态'},
];

const DETAIL_FIELDS: FieldOption[] = [
  {key:'captain',label:'机长'},{key:'copilot',label:'副驾驶'},
  {key:'aircraftReg',label:'航空器注册号'},{key:'takeoffAirport',label:'起飞机场'},
  {key:'landingAirport',label:'降落机场'},{key:'landingCount',label:'起落次数'},
  {key:'dayLanding',label:'昼间起落'},{key:'nightLanding',label:'夜间起落'},
  {key:'simulatorNum',label:'模拟机编号'},{key:'trainingType',label:'训练类型'},
];

const ALL_SORT_FIELDS = [
  {value:'airDate',label:'飞行日期'},{value:'airNum',label:'航班号'},
  {value:'airModel',label:'机型'},{value:'recordType',label:'记录类型'},
  {value:'flightTime',label:'飞行时间'},{value:'updateTime',label:'更新时间'},
];

const PAGE_SIZE_OPTIONS = [10,20,50,100];

function FieldSelector({fields, selected, onChange}: {fields:FieldOption[];selected:string[];onChange:(f:string[])=>void}) {
  const toggle = (key:string) => {
    onChange(selected.includes(key) ? selected.filter(f=>f!==key) : [...selected,key]);
  };
  const allSelected = fields.every(f=>selected.includes(f.key));
  const toggleAll = () => {
    if (allSelected) onChange(selected.filter(f=>!fields.some(fd=>fd.key===f)));
    else onChange([...selected,...fields.filter(f=>!selected.includes(f.key)).map(f=>f.key)]);
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
          {allSelected ? '取消全选' : '全选'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {fields.map(field=>(
          <label key={field.key}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
              selected.includes(field.key) ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}>
            <input type="checkbox" className="hidden" checked={selected.includes(field.key)} onChange={()=>toggle(field.key)} />
            {selected.includes(field.key)
              ? <CheckSquare className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" />
              : <Square className="w-3.5 h-3.5 flex-shrink-0 text-gray-300" />}
            {field.label}
          </label>
        ))}
      </div>
    </div>
  );
}

interface ConfigFormData {
  name: string; selectedFields: string[]; sortBy: string; sortOrder: string;
  isDefault: boolean; pageSize: number; filters: Record<string, string>;
}

const DEFAULT_FORM: ConfigFormData = {
  name:'',selectedFields:BASE_FIELDS.map(f=>f.key),sortBy:'airDate',sortOrder:'desc',
  isDefault:false,pageSize:20,filters:{},
};

export default function FsopViewConfigPage() {
  const {toast} = useToast();
  const { showDialog, setShowDialog } = useSubscriptionGuard();

  const [configs, setConfigs] = useState<ViewConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number|null>(null);
  const [form, setForm] = useState<ConfigFormData>(DEFAULT_FORM);
  const [filterOpts, setFilterOpts] = useState({recordTypes:[] as string[],airNums:[] as string[],airModels:[] as string[],airlines:[] as string[]});
  const [optsLoading, setOptsLoading] = useState(false);
  const optsFetched = useRef(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fsopApi.getViewConfigs({ pageSize: 100 });
      // 后端返回标准分页格式: { count, results, next, previous }
      setConfigs(r.data?.results ?? []);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      } else {
        toast({ title: '获取视图配置失败', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    setOptsLoading(true);
    try {
      const res = await fsopApi.getFilterOptions();
      // 后端返回 camelCase 格式: { success, data: { recordTypes, airNums, airModels, airlines } }
      const d = res.data?.data ?? {};
      setFilterOpts({
        recordTypes: d.recordTypes ?? [],
        airNums: d.airNums ?? [],
        airModels: d.airModels ?? [],
        airlines: d.airlines ?? [],
      });
    } catch (err: any) {
      if (err.response?.status === 402) {
        setShowDialog(true);
      } else {
        toast({ title: '获取筛选选项失败', variant: 'destructive' });
      }
    } finally {
      setOptsLoading(false);
    }
  }, []);

  useEffect(()=>{ fetchConfigs(); },[fetchConfigs]);
  useEffect(()=>{
    if (!optsFetched.current) { optsFetched.current=true; fetchFilterOptions(); }
  },[fetchFilterOptions]);

  const openEdit = (cfg?:ViewConfig) => {
    fetchFilterOptions();
    if (cfg) {
      setEditingId(cfg.id);
      setForm({name:cfg.name,selectedFields:cfg.selectedFields||[],sortBy:cfg.sortBy,sortOrder:cfg.sortOrder,
        isDefault:cfg.isDefault,pageSize:cfg.pageSize,filters:(cfg.filters||{}) as Record<string,string>});
    } else {
      setEditingId(null); setForm(DEFAULT_FORM);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({title:'请输入视图名称',variant:'destructive'}); return; }
    if (form.selectedFields.length===0) { toast({title:'请至少选择一个显示字段',variant:'destructive'}); return; }
    setSaving(true);
    try {
      if (editingId) {
        await fsopApi.updateViewConfig(editingId, form);
        toast({title:'视图已更新'});
      } else {
        await fsopApi.createViewConfig(form);
        toast({title:'视图已创建'});
      }
      setEditingId(null); setForm(DEFAULT_FORM); fetchConfigs();
    } catch (e:any) {
      if (e?.response?.status === 402) {
        setShowDialog(true);
      } else {
        toast({title:e?.response?.data?.message||'保存失败',variant:'destructive'});
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id:number) => {
    if (!confirm('确定要删除这个视图配置吗？')) return;
    try { await fsopApi.deleteViewConfig(id); toast({title:'视图已删除'}); fetchConfigs(); }
    catch (e:any) {
      if (e?.response?.status === 402) {
        setShowDialog(true);
      } else {
        toast({title:e?.response?.data?.message||'删除失败',variant:'destructive'});
      }
    }
  };

  const handleSetDefault = async (id:number) => {
    try { await fsopApi.setDefaultViewConfig(id); toast({title:'已设为默认视图'}); fetchConfigs(); }
    catch (e:any) {
      if (e?.response?.status === 402) {
        setShowDialog(true);
      } else {
        toast({title:e?.response?.data?.message||'设置失败',variant:'destructive'});
      }
    }
  };

  const Fo = ({label,opts,val}:{label:string;opts:string[];val:string}) => (
    <div>
      <Label className="text-xs text-gray-500 mb-1 block">{label}</Label>
      <select className="w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100"
        value={val} onChange={e=>setForm(f=>({...f,filters:{...f.filters,[label]:e.target.value}}))}
        disabled={saving||optsLoading}>
        <option value="">不限</option>
        {opts.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="container mx-auto py-6 max-w-5xl">
      <SubscriptionDialog isOpen={showDialog} onClose={() => setShowDialog(false)} />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <LayoutGrid className="w-6 h-6 text-white" />
          </div>
          <div><h1 className="text-2xl font-bold text-gray-900">视图配置管理</h1>
            <p className="text-sm text-gray-500">自定义飞行记录的显示字段、排序和分页</p>
          </div>
        </div>
        <Button onClick={()=>openEdit()}><Plus className="mr-2 h-4 w-4" />新建视图</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* 视图列表 */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">已有视图</h3>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
           : configs.length===0 ? (
            <Card><CardContent className="py-8 text-center text-sm text-gray-400">暂无视图配置<br/>点击上方「新建视图」创建</CardContent></Card>
          ) : configs.map(cfg=>(
            <Card key={cfg.id} className={`cursor-pointer transition-all hover:shadow-md ${editingId===cfg.id?'ring-2 ring-blue-400':''}`} onClick={()=>openEdit(cfg)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{cfg.name}</span>
                    {cfg.isDefault && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />}
                  </div>
                  <div className="flex gap-1" onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>handleSetDefault(cfg.id)} className="p-1 hover:bg-gray-100 rounded" title="设为默认">
                      {cfg.isDefault ? <StarOff className="w-4 h-4 text-gray-400" /> : <Star className="w-4 h-4 text-gray-300 hover:text-amber-400" />}
                    </button>
                    <button onClick={()=>handleDelete(cfg.id)} className="p-1 hover:bg-red-50 rounded" title="删除">
                      <Trash2 className="w-4 h-4 text-gray-300 hover:text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-1">{cfg.selectedFields?.length||0} 个字段 · {cfg.pageSize} 条/页</div>
                <div className="text-xs text-gray-400">排序: {cfg.sortBy} ({cfg.sortOrder})</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* 编辑表单 */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{editingId?'编辑视图':'新建视图'}</CardTitle>
              <CardDescription>选择要在「飞行记录」页面中显示的字段及其排列顺序</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="view-name">视图名称</Label>
                <Input id="view-name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                  placeholder="如: 我的常用视图" disabled={saving} />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">基础字段</Label>
                  <span className="text-xs text-gray-400">直接存储在主表的字段</span>
                </div>
                <FieldSelector fields={BASE_FIELDS} selected={form.selectedFields}
                  onChange={selectedFields=>setForm(f=>({...f,selectedFields}))} />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">明细字段</Label>
                  <span className="text-xs text-gray-400">存储在 detail JSON 中的字段</span>
                </div>
                <FieldSelector fields={DETAIL_FIELDS} selected={form.selectedFields}
                  onChange={selectedFields=>setForm(f=>({...f,selectedFields}))} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><ArrowUpDown className="w-3.5 h-3.5 inline mr-1"/>排序字段</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.sortBy}
                    onChange={e=>setForm(f=>({...f,sortBy:e.target.value}))} disabled={saving}>
                    {ALL_SORT_FIELDS.map(f=><option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>排序方向</Label>
                  <div className="flex gap-4 pt-2">
                    {[['desc','降序 (新→旧)'],['asc','升序 (旧→新)']].map(([v,l])=>(
                      <label key={v} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" className="accent-blue-600"
                          checked={form.sortOrder===v} onChange={()=>setForm(f=>({...f,sortOrder:v}))}
                          disabled={saving} />
                        <span className="text-sm">{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="page-size">每页条数</Label>
                <select id="page-size" className="border rounded-lg px-3 py-2 text-sm" value={form.pageSize}
                  onChange={e=>setForm(f=>({...f,pageSize:Number(e.target.value)}))} disabled={saving}>
                  {PAGE_SIZE_OPTIONS.map(n=><option key={n} value={n}>{n} 条/页</option>)}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <Switch id="is-default" checked={form.isDefault}
                  onCheckedChange={isDefault=>setForm(f=>({...f,isDefault}))} disabled={saving} />
                <Label htmlFor="is-default" className="cursor-pointer">设为默认视图</Label>
              </div>

              {/* 筛选条件 */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <Label className="text-sm font-medium">筛选条件</Label>
                  <span className="text-xs text-gray-400">（保存后，此视图将固定使用以下筛选）</span>
                  {optsLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Fo label="recordType" opts={filterOpts.recordTypes} val={form.filters.recordType||''} />
                  <Fo label="airNum" opts={filterOpts.airNums} val={form.filters.airNum||''} />
                  <Fo label="airModel" opts={filterOpts.airModels} val={form.filters.airModel||''} />
                </div>
                <p className="text-xs text-gray-400">
                  筛选条件将保存到此视图配置中，切换到此视图时自动应用。留空则不限制。
                  {optsLoading && ' 正在加载选项...'}
                  {!optsLoading && filterOpts.recordTypes.length===0 && filterOpts.airNums.length===0 && ' 暂无筛选数据，请先同步飞行记录。'}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingId?'保存修改':'创建视图'}
                </Button>
                {editingId && <Button variant="outline" onClick={()=>{setEditingId(null);setForm(DEFAULT_FORM);}}>取消</Button>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
